import { useMonarchMetadata } from "@/context/MonarchMetadataContext";
import cn from "@/lib/cn";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import uppercaseFirstLetter from "@/lib/uppercase-first-letter";
import React, { useEffect, useRef, useState } from "react";
import type { TabListProps, TabType } from "./types";

const getTabId = (tabId: string) => `bento-tab-${tabId}`;
const getTabPanelId = (tabId: string) => `bento-tabpanel-${tabId}`;

function revealTab(
  strip: HTMLDivElement | null,
  tab: HTMLButtonElement | null
) {
  if (!strip || !tab) return;
  const bounds = strip.getBoundingClientRect();
  const target = tab.getBoundingClientRect();
  const gutter = 4;
  const left = bounds.left + gutter;
  const right = bounds.right - gutter;
  if (target.left < left || target.width > right - left) {
    strip.scrollLeft += target.left - left;
  } else if (target.right > right) {
    strip.scrollLeft += target.right - right;
  }
}

const TabList: React.FC<TabListProps> = ({
  tabs,
  activeTab,
  onTabChange,
  onTabIntent,
  className = "",
  tagularProps,
}) => {
  const [, setIsScrolling] = useState(false);
  const [focusedTabId, setFocusedTabId] = useState<TabType | undefined>();
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef(new Map<TabType, HTMLButtonElement>());
  const focusWithinRef = useRef(false);
  const preloadedImagesRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const contextMonarchMetadata = useMonarchMetadata();
  const resolvedMonarchMetadata =
    tagularProps?.monarchMetadata ?? contextMonarchMetadata;
  const selection = tabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : tabs[0]?.id;
  const focusId = tabs.some((tab) => tab.id === focusedTabId)
    ? focusedTabId
    : selection;

  // Preload images for a specific tab
  const preloadTabImages = (tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId);
    if (!tab) return;

    const imageUrls: string[] = [];

    // Collect all background images from cards
    tab.cards.forEach((card) => {
      if (card.backgroundImage) {
        imageUrls.push(card.backgroundImage);
      }
      if (card.mobileBackgroundImage) {
        imageUrls.push(card.mobileBackgroundImage);
      }
    });

    // Preload images that haven't been preloaded yet
    imageUrls.forEach((url) => {
      if (!preloadedImagesRef.current.has(url)) {
        // Method 1: Create link preload hint (best for modern browsers)
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = url;
        link.fetchPriority = "low"; // Don't block critical resources
        document.head.appendChild(link);

        // Method 2: Keep strong reference to Image object to prevent GC
        const img = new Image();
        img.fetchPriority = "low";
        img.src = url;

        // Store the image object to prevent garbage collection
        preloadedImagesRef.current.set(url, img);
      }
    });
  };

  useEffect(() => {
    const revealId = focusWithinRef.current ? focusId : selection;
    if (revealId) {
      revealTab(
        scrollContainerRef.current,
        buttonsRef.current.get(revealId) ?? null
      );
    }
  }, [focusId, selection]);

  useEffect(() => {
    if (!focusId) {
      focusWithinRef.current = false;
      return;
    }

    if (focusWithinRef.current && focusedTabId !== focusId) {
      buttonsRef.current.get(focusId)?.focus({ preventScroll: true });
    }
  }, [focusId, focusedTabId]);

  const handleScroll = () => {
    setIsScrolling(true);

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  };

  const handleInteractionStart = () => {
    setIsScrolling(true);
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  };

  const handleInteractionEnd = () => {
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 1000);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    tabId: TabType
  ) => {
    const index = tabs.findIndex((tab) => tab.id === tabId);
    if (index < 0) return;

    let nextIndex: number;
    switch (event.key) {
      case "ArrowRight":
        nextIndex = (index + 1) % tabs.length;
        break;
      case "ArrowLeft":
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    buttonsRef.current.get(tabs[nextIndex].id)?.focus({ preventScroll: true });
  };

  return (
    <div
      ref={scrollContainerRef}
      role="tablist"
      aria-orientation="horizontal"
      className={cn(
        "flex justify-start md:justify-center items-center pb-3 whitespace-nowrap",
        // !isScrolling && "scrollbar-hide",
        "overflow-x-auto overflow-y-visible",
        className
      )}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          focusWithinRef.current = false;
          setFocusedTabId(undefined);
          if (selection) {
            revealTab(
              scrollContainerRef.current,
              buttonsRef.current.get(selection) ?? null
            );
          }
        }
      }}
      onScroll={handleScroll}
      onTouchStart={handleInteractionStart}
      onTouchEnd={handleInteractionEnd}
      style={{
        WebkitOverflowScrolling: "touch",
      }}
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-white px-2 py-2 border-2 border-neutral-200 !shadow-[0_12px_12px_-10px_rgba(0,0,0,0.28)]">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            ref={(button) => {
              if (button) buttonsRef.current.set(tab.id, button);
              else buttonsRef.current.delete(tab.id);
            }}
            role="tab"
            onClick={() => {
              onTabChange(tab.id);
              fireElementClicked({
                actionOutcome: "tabChanged",
                elementType: "tab",
                text: tab.label,
                position: index + 1,
                name: `tab${tagularProps?.location ? uppercaseFirstLetter(tagularProps?.location) : ""}${tab.label}`,
                ...tagularProps,
                ...(resolvedMonarchMetadata && {
                  monarchMetadata: resolvedMonarchMetadata,
                }),
              });
            }}
            onFocus={(event) => {
              focusWithinRef.current = true;
              setFocusedTabId(tab.id);
              onTabIntent?.(tab.id);
              preloadTabImages(tab.id);
              revealTab(scrollContainerRef.current, event.currentTarget);
            }}
            onKeyDown={(event) => handleKeyDown(event, tab.id)}
            onMouseEnter={() => {
              onTabIntent?.(tab.id);
              preloadTabImages(tab.id);
            }}
            aria-selected={selection === tab.id}
            aria-controls={getTabPanelId(tab.id)}
            id={getTabId(tab.id)}
            data-testid={`INLINE-TAB-Tabbed Bento - ${tab.label} - PSHSD`}
            tabIndex={focusId === tab.id ? 0 : -1}
            className={cn(
              "flex-shrink-0 px-4 py-2 rounded-[32px] font-medium transition-all duration-200 text-base cursor-pointer whitespace-nowrap",
              selection === tab.id
                ? "bg-purple-50 text-primary border-2 border-primary"
                : "bg-transparent text-black hover:bg-gray-50"
            )}
          >
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default TabList;
